// Bulk SMS — outbound transactional / promotional SMS to subscribers
// via a vendor-pluggable backend (MiMSMS V2 today). The page lives at
// `/bulk-sms`. Go side: internal/bulksmsstatus.

import type { ApiClient } from "./client.js";
import type {
  BulkSmsBalanceResponse,
  BulkSmsCampaign,
  BulkSmsCampaignListResponse,
  BulkSmsCampaignSaveResponse,
  BulkSmsJobDetailResponse,
  BulkSmsJobsResponse,
  BulkSmsRecipientFilter,
  BulkSmsRecipientsResponse,
  BulkSmsSaveRequest,
  BulkSmsSaveResponse,
  BulkSmsSendRequest,
  BulkSmsSendResponse,
  BulkSmsStateResponse,
  BulkSmsTemplate,
  BulkSmsTestResponse,
} from "./types.js";

export type { BulkSmsTemplate, BulkSmsCampaign };

export function makeBulkSmsApi(client: ApiClient) {
  function creds(req: BulkSmsSaveRequest): Record<string, unknown> {
    const b: Record<string, unknown> = {
      backend_kind: req.backend_kind ?? "api:mim",
      username: req.username,
      default_sender_id: req.default_sender_id ?? "",
    };
    if (req.api_key) b.api_key = req.api_key;
    if (req.extra) b.extra = req.extra;
    return b;
  }
  return {
    /** Page payload — provider config (no secrets), saved sender id,
     *  default txn type, rolling-10 health, available backends. */
    state() {
      return client.get<BulkSmsStateResponse>("/v1/api/bulksms/state");
    },
    /** Most-recent N jobs (default 50). */
    jobs(limit?: number) {
      return client.get<BulkSmsJobsResponse>("/v1/api/bulksms/jobs", limit ? { limit } : undefined);
    },
    /** One job's header + per-recipient rows. */
    job(id: number) {
      return client.get<BulkSmsJobDetailResponse>("/v1/api/bulksms/job", { id });
    },
    /** Save credentials + defaults — admin only.
     *  Omit / empty `api_key` to keep the existing ciphertext. */
    save(req: BulkSmsSaveRequest) {
      return client.post<BulkSmsSaveResponse>("/v1/api/bulksms/save", creds(req));
    },
    /** Probe connectivity (BalanceCheck against saved creds) — admin only. */
    test() {
      return client.post<BulkSmsTestResponse>("/v1/api/bulksms/test", {});
    },
    /** Live balance refresh — admin OR support. */
    balance() {
      return client.post<BulkSmsBalanceResponse>("/v1/api/bulksms/balance", {});
    },
    /** Compose + send. The server parses `recipients` (free-form paste:
     *  newline / comma / semicolon / pipe delimited 01XXXXXXXXX) and
     *  rejects the call only when zero recipients survive normalisation.
     *  For promotional sends (txn_type=P), campaign_id is required and
     *  the server validates the body matches the approved template
     *  verbatim. */
    send(req: BulkSmsSendRequest) {
      return client.post<BulkSmsSendResponse>("/v1/api/bulksms/send", req);
    },
    /** Approved promotional campaigns — the catalog the compose form's
     *  P dropdown reads. List is admin+support; save/delete are admin. */
    listCampaigns() {
      return client.get<BulkSmsCampaignListResponse>("/v1/api/bulksms/campaigns");
    },
    saveCampaign(req: { id?: number; label: string; campaign_id: string; sender_id: string; approved_body: string }) {
      return client.post<BulkSmsCampaignSaveResponse>("/v1/api/bulksms/campaigns/save", {
        id: req.id ?? 0,
        label: req.label,
        campaign_id: req.campaign_id,
        sender_id: req.sender_id,
        approved_body: req.approved_body,
      });
    },
    deleteCampaign(id: number) {
      return client.post<{ ok: boolean; error?: string }>("/v1/api/bulksms/campaigns/delete", { id });
    },
    /** Quick-load recipients: pulls subscribers from radius_subscribers
     *  matching a preset filter, returns one row per match with the
     *  mobile already normalised to 880XXXXXXXXX. The compose page uses
     *  this to populate the Bulk-mode textarea with one click. */
    recipients(filter: BulkSmsRecipientFilter = "active") {
      return client.get<BulkSmsRecipientsResponse>("/v1/api/bulksms/recipients", { filter });
    },
  };
}

export type BulkSmsApi = ReturnType<typeof makeBulkSmsApi>;
